<template>
  <div class="container my-5">
    <div class="row">
      <div class="col-md-6 mb-4">
        <div class="card card-widget h-100">
          <div class="card-body">
            <h3 class="widget-title">Current Balance</h3>
            <p class="lead mb-0">{{ budget.balance }} <small class="text-muted">hrs</small></p>
          </div>
        </div>
      </div>
    </div>

    <h2>Invoices</h2>

    <c-data-list :items="budget.invoices || []" class="no-links mb-5">
      <template v-slot:columns>
        <div class="col-6 col-lg-2">
          <p>Number</p>
        </div>
        <div class="col-6 col-lg-2">
          <p>From</p>
        </div>
        <div class="col-6 col-lg-2">
          <p>To</p>
        </div>
        <div class="col-6 col-lg-2">
          <p>Hours</p>
        </div>
        <div class="col-6 col-lg-2">
          <p>Amount</p>
        </div>
        <div class="col-6 col-lg-2 text-lg-end">
          <p>Status</p>
        </div>
      </template>
      <template v-slot:default="{ item }">
        <div class="row">
          <div class="col-6 col-lg-2">
            <p>
              <span class="data-label d-lg-none">Number:</span>
              <span v-if="item.xero_number" class="strong">{{ item.xero_number }}</span>
              <span v-else class="status status-info"><i class="fa fa-spinner fa-spin"></i> Syncing</span>
            </p>
          </div>
          <div class="col-6 col-lg-2">
            <p>
              <span class="data-label d-lg-none">From:</span>
              {{ item.pivot.start | moment('DD/MM/YYYY') }}
            </p>
          </div>
          <div class="col-6 col-lg-2">
            <p>
              <span class="data-label d-lg-none">To:</span>
              {{ item.pivot.end | moment('DD/MM/YYYY') }}
            </p>
          </div>
          <div class="col-6 col-lg-2">
            <p>
              <span class="data-label d-lg-none">Hours:</span>
              {{ item.pivot.hours_billed }}
            </p>
          </div>
          <div class="col-6 col-lg-2">
            <p>
              <span class="data-label d-lg-none">Amount:</span>
              ${{ item.total | currency }}
            </p>
          </div>
          <div class="col-6 col-lg-2 text-lg-end">
            <p>
              <span class="data-label d-lg-none">Status:</span>
              {{ item.status }}
            </p>
          </div>
        </div>
      </template>
      <template v-slot:empty>
        <li class="py-4">
          <p class="text-center">No invoices have been raised.</p>
        </li>
      </template>
    </c-data-list>

    <div class="row mb-3">
      <div class="col-12 col-lg d-flex align-items-center">
        <h2 class="mb-0">Billable Items</h2>
      </div>
      <div class="col-12 col-lg-auto">
        <div class="row g-2 align-items-center">
          <div class="col">
            <input v-model="start" type="date" class="form-control" aria-label="From" />
          </div>
          <div class="col">
            <input v-model="end" type="date" class="form-control" aria-label="To" />
          </div>
          <div class="col-auto">
            <c-button @click="loadData" class="btn btn-rounded btn-primary">Search</c-button>
          </div>
        </div>
      </div>
    </div>

    <c-data-list :items="items" key-by="key" class="no-links mb-4">
      <template v-slot:columns>
        <div class="col-3 col-lg-2">
          <p>Ticket</p>
        </div>
        <div class="col-9 col-lg-8">
          <p>Title</p>
        </div>
        <div class="col-12 col-lg-2 text-lg-end">
          <p>Spent</p>
        </div>
      </template>
      <template v-slot:default="{ item }">
        <div class="row pointer" @click="toggle(item.key)">
          <div class="col-3 col-lg-2">
            <p>
              <i class="far fa-fw me-1" :class="expanded[item.key] ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
              <a :href="yodizUrl(item)" target="_blank" rel="noopener" @click.stop>#{{ item.yodiz_id }}</a>
            </p>
          </div>
          <div class="col-9 col-lg-8">
            <p>{{ item.title }}</p>
          </div>
          <div class="col-12 col-lg-2 text-lg-end">
            <p>
              <span class="data-label d-lg-none">Spent:</span>
              {{ item.spent }} hrs
            </p>
          </div>
        </div>

        <!-- Wrapped so list-data's flex styling on direct .row > col children doesn't apply -->
        <div v-if="expanded[item.key]" class="time-entries mt-2 pt-2 border-top">
          <p class="strong mb-2">Time Entries</p>
          <div v-for="timeEntry in item.time_entries" :key="timeEntry.id" class="row small">
            <div class="col-4 col-lg-2">
              <p>{{ timeEntry.entry_date | moment('DD/MM/YYYY') }}</p>
            </div>
            <div class="col-5 col-lg-8">
              <p>{{ timeEntry.user ? timeEntry.user.name : 'Unknown' }}</p>
            </div>
            <div class="col-3 col-lg-2 text-end">
              <p>{{ timeEntry.spent }} hrs</p>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:empty>
        <li class="py-4">
          <p class="text-center">No unbilled time for this period.</p>
        </li>
      </template>
    </c-data-list>

    <div class="card mb-4">
      <div class="card-body">
        <div class="row align-items-end">
          <div class="col-12 col-md-4 mb-3 mb-md-0">
            <label class="control-label">Total Hours to be billed</label>
            <input
              name="totalHours"
              v-model="totalHours"
              type="number"
              class="form-control"
              data-vv-validate-on="input|blur"
              data-vv-as="total hours"
              v-validate="'required'"
            />
          </div>
          <div class="col-12 col-md-4 mb-3 mb-md-0">
            <p class="lead mb-0">${{ totalHours * hourlyRate | currency }} <small class="text-muted">ex GST</small></p>
          </div>
          <div class="col-12 col-md-4 text-md-end">
            <c-button @click="handleGenerateInvoice" class="btn btn-rounded btn-primary">Generate Invoice</c-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { notify } from 'utils'
import { mapState, mapActions } from 'vuex'

export default {
  $_veeValidate: {
    validator: 'new' // give me my own validator scope.
  },

  data () {
    return {
      start: null,
      end: null,
      totalHours: 0,
      hourlyRate: 185,
      expanded: {}
    }
  },

  computed: {
    ...mapState('budget', {
      budget: 'single'
    }),
    ...mapState('budget/billableItems', {
      billableItems: 'list'
    }),

    items () {
      return this.billableItems.map(item => ({ ...item, key: `${item.type}-${item.yodiz_id}` }))
    }
  },

  mounted () {
    this.loadData()
  },

  methods: {
    ...mapActions('budget/billableItems', {
      getBillableItems: 'getList',
      generateInvoice: 'addSingle'
    }),

    loadData () {
      return this.getBillableItems({ start: this.start, end: this.end }).then(() => {
        this.expanded = {}
        this.totalHours = this.billableItems.reduce((prev, cur) => prev + cur.spent, 0).toFixed(2)
      })
    },

    toggle (key) {
      this.$set(this.expanded, key, !this.expanded[key])
    },

    yodizUrl (item) {
      const prefix = item.type == 'Story' ? 'us' : 'bg'

      return `https://app.yodiz.com/plan/pages/board.vz#/app/${prefix}-${item.yodiz_id}`
    },

    async handleGenerateInvoice () {
      if (!(await this._validate())) {
        notify.error('Form validation failed, please check your inputs and try again.')
        return Promise.reject()
      }

      return this.generateInvoice({
        start: this.start,
        end: this.end,
        total_hours: this.totalHours,
        billable_items: this.billableItems
      }).then(() => {
        this.$bus.$emit('budget-reload-data', this.budget.id)
        this.loadData()
      }).catch((error) => {
        if (error.response && error.response.status === 422) {
          notify.error('Form validation failed, please check your inputs and try again.')
        } else {
          console.error(error)
          notify.error('Failed to create invoice, please try again.')
        }
      })
    }
  }
}
</script>

<style scoped>
.pointer {
  cursor: pointer;
}
</style>
